// KovrificationConfig component

import { useTranslation } from 'react-i18next';
import { messages } from '../../messages';

interface CaptionConfig {
  color?: string;
  accent_color?: string;
  font_size?: number;
  accent_font_size?: number;
  words_per_line?: number;
  vertical_margin?: number;
  style_id?: string;
}

interface VideoProcessingConfig {
  vertical_video_proportion: number;
}

interface KovrificationConfigProps {
  config: {
    captions_enabled?: boolean;
    caption_config?: CaptionConfig;
    video_processing?: VideoProcessingConfig;
  };
}

/**
 * KovrificationConfig component
 * 
 * A component that displays the configuration for the kovrification process.
 */
export function KovrificationConfig({ config }: KovrificationConfigProps) {
  const captionConfig: CaptionConfig = config.caption_config || {};
  const videoConfig = config.video_processing || { vertical_video_proportion: 0.7 };
  const { t } = useTranslation();
  
  return (
    <div className="space-y-6">
      {config.captions_enabled && (
        <section>
          <h4 className="text-base font-medium text-content-strong mb-4 flex items-center gap-2">
            <span className="material-symbols-outlined text-primary">closed_caption</span>
            {messages.create.configuration.captions.title}
          </h4>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
            <div className="bg-surface-subtle dark:bg-surface-subtle-dark p-4 rounded-xl">
              <div className="flex justify-between items-center mb-3">
                <label className="text-body-strong text-sm">
                  {t('kovrificationConfig.primaryFontColorLabel')}
                </label>
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 rounded-full border border-default shadow-sm"
                    style={{ backgroundColor: captionConfig.color }}>
                  </div>
                  <span className="text-xs font-mono text-body">{captionConfig.color}</span>
                </div>
              </div>
              
              <div className="bg-media-canvas rounded-lg p-3 flex items-center justify-center">
                <span className="font-semibold" style={{ color: captionConfig.color }}>
                  {t('kovrificationConfig.textPreview')}
                </span>
              </div>
            </div>
            
            <div className="bg-surface-subtle dark:bg-surface-subtle-dark p-4 rounded-xl">
              <div className="flex justify-between items-center mb-3">
                <label className="text-body-strong text-sm">
                  {t('kovrificationConfig.accentFontColorLabel')}
                </label>
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 rounded-full border border-default shadow-sm"
                    style={{ backgroundColor: captionConfig.accent_color }}>
                  </div>
                  <span className="text-xs font-mono text-body">{captionConfig.accent_color}</span>
                </div>
              </div>
              
              <div className="bg-media-canvas rounded-lg p-3 flex items-center justify-center">
                <span className="font-semibold" style={{ color: captionConfig.accent_color }}>
                  {t('kovrificationConfig.accentPreview')}
                </span>
              </div>
            </div>
          </div>
          
          <div className="bg-surface-subtle dark:bg-surface-subtle-dark p-4 rounded-xl mb-6">
            <div className="flex justify-between items-center mb-3">
              <label className="text-body-strong">
                {messages.create.configuration.captions.wordsPerLine}
              </label>
              <div className="flex items-center gap-2">
                <span className="w-8 h-8 flex items-center justify-center bg-primary text-on-primary rounded-full font-medium">
                  {captionConfig.words_per_line}
                </span>
              </div>
            </div>
          </div>
        </section>
      )}

      <section>
        <h4 className="text-base font-medium text-content-strong mb-4 flex items-center gap-2">
          <span className="material-symbols-outlined text-primary">aspect_ratio</span>
          {t('kovrificationConfig.videoSizeTitle')}
        </h4>
        
        <div className="bg-surface-subtle dark:bg-surface-subtle-dark p-4 rounded-xl">
          <div className="flex justify-between items-center mb-3">
            <label className="text-body-strong text-sm">
              {t('kovrificationConfig.verticalProportionLabel')}
            </label>
            <div className="text-sm font-medium text-body">
              {Math.round(videoConfig.vertical_video_proportion * 100)}%
            </div>
          </div>
          
          <div className="w-full aspect-[9/16] bg-neutral-fill dark:bg-neutral-fill-dark rounded-lg relative overflow-hidden mb-3">
            <div 
              className="absolute top-0 left-0 right-0 bg-primary-alpha-20"
              style={{ height: `${videoConfig.vertical_video_proportion * 100}%` }}
            ></div>
            <div
              className="absolute w-full border-t-2 border-primary"
              style={{ top: `${videoConfig.vertical_video_proportion * 100}%` }}
            ></div>
          </div>
          
          <p className="text-xs text-theme-hint">
            {t('kovrificationConfig.verticalProportionDescription')}
          </p>
        </div>
      </section>
    </div>
  );
} 